import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { SpacesControllerService } from '@osos/shared/api';
import {
  LoadingIndicator,
  LoadingType,
  ManagedHttpContext,
  ROOT_SPACE_ID,
  SpaceEntryPresenterModel,
} from '@osos/shared/types';
import { finalize, map, tap } from 'rxjs/operators';
import { SpaceEntryFactoryService } from '../../factories/index';
import { CoreSpaceActions } from './core-space.actions';

export interface CoreSpaceStateModel {
  mySpaces?: SpaceEntryPresenterModel[];
  activeSpace?: SpaceEntryPresenterModel;
  loading?: LoadingIndicator;
}

@State<CoreSpaceStateModel>({
  name: 'coreSpace',
  defaults: {
    mySpaces: [],
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class CoreSpaceState {
  constructor(
    private spaceEntryFactoryService: SpaceEntryFactoryService,
    private spaceService: SpacesControllerService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------
  @Selector([CoreSpaceState])
  static mySpaces(state: CoreSpaceStateModel): SpaceEntryPresenterModel[] {
    return state.mySpaces;
  }

  @Selector([CoreSpaceState])
  static activeSpace(state: CoreSpaceStateModel): SpaceEntryPresenterModel {
    return state.activeSpace;
  }

  static loading(type: LoadingType) {
    return createSelector(
      [CoreSpaceState],
      (state: CoreSpaceStateModel) => state.loading[type],
    );
  }
  // --------------------------------- ACTIONS --------------------------------

  @Action(CoreSpaceActions.MySpaces)
  mySpaces({ setState }: StateContext<CoreSpaceStateModel>) {
    setState(
      patch<CoreSpaceStateModel>({
        mySpaces: [],
        activeSpace: null,
        loading: patch<LoadingIndicator>({
          fetch: true,
        }),
      }),
    );
    return this.spaceService
      .getSpacesForCurrentUser({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((counters) => {
          return counters.map((counter) =>
            this.spaceEntryFactoryService.createPresenterModel(counter),
          );
        }),
        tap((mySpaces: SpaceEntryPresenterModel[]) => {
          const domain = window.location.host;
          let activeSpace = mySpaces.find((space) => {
            const url = `${space.domainIdentifier}.${space.url}`;
            return domain.includes(url);
          });
          if (!activeSpace) {
            activeSpace = mySpaces.find((space) => space.id === ROOT_SPACE_ID);
          }
          setState(
            patch<CoreSpaceStateModel>({
              mySpaces,
              activeSpace,
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<CoreSpaceStateModel>({
              loading: patch<LoadingIndicator>({
                fetch: false,
              }),
            }),
          );
        }),
      );
  }
}
